import React, { useState } from "react";
import { useGlobal } from "GlobalContext";

// @material-ui/core components
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { useHistory } from "react-router-dom";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogActions from "@material-ui/core/DialogActions";
import CoreButton from "@material-ui/core/Button";
import PropTypes from "prop-types";

// @material-ui/icons
import PermIdentity from "@material-ui/icons/PermIdentity";

// core components
import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import Button from "components/CustomButtons/Button.js";
import Clearfix from "components/Clearfix/Clearfix.js";
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import CardHeader from "components/Card/CardHeader.js";
import CardIcon from "components/Card/CardIcon.js";
import CardAvatar from "components/Card/CardAvatar.js";

import avatar from "assets/img/noimage.png";
import styles from "assets/jss/material-dashboard-pro-react/views/userProfileStyles.js";

import { useTranslation } from "react-i18next";

const useStyles = makeStyles(styles);
const API_URL = process.env.REACT_APP_API;

/**
 * Bloque reutilizable de dato de perfil.
 *
 * Objetivos:
 * - evitar widths fijos tipo 150/250/300
 * - permitir que cada bloque ocupe el 100% de su celda
 * - dejar que el texto largo parta correctamente
 */
function ProfileField({ classes, label, value, fallback }) {
  return (
    <Box style={{ width: "100%" }}>
      <Typography className={classes.title}>{label}</Typography>
      <div className={classes.separata} style={{ width: "100%" }} />
      <Typography
        className={classes.userInfo}
        style={{ wordBreak: "break-word" }}
      >
        {value || fallback}
      </Typography>
    </Box>
  );
}

ProfileField.propTypes = {
  classes: PropTypes.shape({
    title: PropTypes.string,
    separata: PropTypes.string,
    userInfo: PropTypes.string,
  }).isRequired,
  label: PropTypes.node.isRequired,
  value: PropTypes.node,
  fallback: PropTypes.node.isRequired,
};

export default function UserProfile() {
  const classes = useStyles();
  const history = useHistory();
  const theme = useTheme();
  const fullScreenDialog = useMediaQuery(theme.breakpoints.down("sm"));

  const { sharedValue, clearUserData } = useGlobal();
  const {
    id,
    username,
    nombre,
    email,
    rol,
    biografia,
    fotoPerfil,
    institution,
  } = sharedValue || {};

  const { t } = useTranslation("myProfile");

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [isError, setError] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  /**
   * Eliminación de cuenta.
   *
   * Se corrige un bug importante del código anterior:
   * antes se limpiaba el usuario y se recargaba la página incluso
   * cuando el DELETE fallaba.
   *
   * Ahora solo se limpia el estado y se redirige si la respuesta es correcta.
   */
  const deleteRequest = async () => {
    if (isDeleting) return;

    setError(null);
    setIsDeleting(true);

    try {
      const editResp = await fetch(`/api/usuarios/${id}`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
      });

      const response = await editResp.json();

      if (response.ok !== true) {
        setError(response.mensaje);
        return;
      }

      setDeleteOpen(false);
      clearUserData();
      history.push("/");
      window.location.reload();
    } catch (err) {
      setError(err.message);
      console.error(err);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div>
      <Dialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        aria-labelledby="delete-account-dialog-title"
        fullWidth
        maxWidth="xs"
        fullScreen={fullScreenDialog}
      >
        <DialogTitle id="delete-account-dialog-title">
          {t("myprofile-delete-dialog-title")}
        </DialogTitle>

        <DialogContent>
          <DialogContentText>
            {t("myprofile-delete-dialog-text")}
          </DialogContentText>

          {isError ? (
            <Typography
              variant="body2"
              style={{ color: "red", marginTop: 12, wordBreak: "break-word" }}
            >
              {isError}
            </Typography>
          ) : null}
        </DialogContent>

        <DialogActions>
          <CoreButton onClick={() => setDeleteOpen(false)} color="default">
            {t("myprofile-delete-dialog-cancel")}
          </CoreButton>
          <CoreButton
            color="secondary"
            onClick={deleteRequest}
            disabled={isDeleting}
          >
            {t("myprofile-delete-dialog-confirm")}
          </CoreButton>
        </DialogActions>
      </Dialog>

      <GridContainer spacing={3}>
        {/* Columna principal con datos */}
        <GridItem xs={12} sm={12} md={8}>
          <Card>
            <CardHeader color="primary" icon>
              <CardIcon color="primary">
                <PermIdentity />
              </CardIcon>
              <h4 className={classes.cardIconTitle}>
                {t("myprofile-card-title")}
              </h4>
            </CardHeader>

            <CardBody className={classes.cardMain}>
              {/* Fila 1 */}
              <GridContainer spacing={3}>
                <GridItem xs={12} sm={6} md={4}>
                  <ProfileField
                    classes={classes}
                    label={t("myprofile-label-fullname")}
                    value={nombre}
                    fallback={t("myprofile-fallback-not-provided")}
                  />
                </GridItem>

                <GridItem xs={12} sm={6} md={4}>
                  <ProfileField
                    classes={classes}
                    label={t("myprofile-label-username")}
                    value={username}
                    fallback={t("myprofile-fallback-not-provided")}
                  />
                </GridItem>

                <GridItem xs={12} sm={6} md={4}>
                  <ProfileField
                    classes={classes}
                    label={t("myprofile-label-role")}
                    value={rol}
                    fallback={t("myprofile-fallback-not-provided")}
                  />
                </GridItem>
              </GridContainer>

              {/* Fila 2 */}
              <GridContainer spacing={3}>
                <GridItem xs={12} sm={6}>
                  <ProfileField
                    classes={classes}
                    label={t("myprofile-label-email")}
                    value={email}
                    fallback={t("myprofile-fallback-not-provided")}
                  />
                </GridItem>

                <GridItem xs={12} sm={6}>
                  <ProfileField
                    classes={classes}
                    label={t("myprofile-label-institution")}
                    value={institution}
                    fallback={t("myprofile-fallback-not-provided")}
                  />
                </GridItem>
              </GridContainer>

              {/* Fila 3 */}
              <GridContainer spacing={3}>
                <GridItem xs={12}>
                  <ProfileField
                    classes={classes}
                    label={t("myprofile-label-aboutme")}
                    value={biografia}
                    fallback={t("myprofile-fallback-no-additional-info")}
                  />
                </GridItem>
              </GridContainer>

              {isError ? (
                <Typography
                  variant="body2"
                  style={{ color: "red", marginTop: 12, wordBreak: "break-word" }}
                >
                  {isError}
                </Typography>
              ) : null}

              <Box mt={2}>
                <Button
                  color="primary"
                  className={classes.updateProfileButton}
                  onClick={() => history.push("/profile/edit")}
                >
                  {t("myprofile-action-edit-profile")}
                </Button>
              </Box>

              <Clearfix />
            </CardBody>
          </Card>
        </GridItem>

        {/* Columna lateral */}
        <GridItem xs={12} sm={12} md={4}>
          <Card profile>
            <CardAvatar profile>
              <img
                src={fotoPerfil || avatar}
                alt={t("myprofile-avatar-alt")}
                style={{ objectFit: "cover" }}
              />
            </CardAvatar>

            <CardBody profile>
              <h6 className={classes.cardCategory}>
                {rol || t("myprofile-fallback-not-provided")}
              </h6>

              <h4
                className={classes.cardTitle}
                style={{ wordBreak: "break-word" }}
              >
                {nombre || t("myprofile-fallback-not-provided")}
              </h4>

              <p className={classes.description} style={{ wordBreak: "break-word" }}>
                {biografia || t("myprofile-fallback-no-additional-info")}
              </p>

              <Button color="rose" round onClick={() => setDeleteOpen(true)}>
                {t("myprofile-action-delete-account")}
              </Button>
            </CardBody>
          </Card>
        </GridItem>
      </GridContainer>
    </div>
  );
}